/* ==========================================================================
   2ATG — homepage section behaviour
   --------------------------------------------------------------------------
   Registers modules with the registry in atg-motion.js. Nothing here binds a
   listener directly; each module returns its teardown, so the theme editor
   can re-render a section without leaving dead handlers behind (the bug the
   playbook's model theme shipped — SHOPIFY-PLAYBOOK.md §4.8).

   Loaded globally from theme.liquid rather than from each section. A script
   tag inside a section is not reliably executed when the merchant adds that
   section in the editor, so a section added after page load would render
   and never come alive.
   ========================================================================== */

(function () {
  "use strict";
  if (!window.ATG) return;

  /* ==================================================================
     MODULE: filter
     Upgrades the product discovery tab links into a WAI-ARIA tablist.
     Arrow keys move between tabs, Home/End jump, and the view-all link
     follows the active tab.
     ================================================================== */

  window.ATG.register("filter", function (root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll("[data-atg-tab]"));
    if (tabs.length < 2) return;
    var viewAll = root.querySelector("[data-atg-view-all]");

    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute("aria-selected", on ? "true" : "false");
        t.tabIndex = on ? 0 : -1;
        var panel = document.getElementById(t.getAttribute("aria-controls"));
        if (panel) panel.hidden = !on;
      });
      if (viewAll && tab.dataset.viewAll) viewAll.href = tab.dataset.viewAll;
      if (focus) tab.focus();
      // Keep the chosen tab in view when the row is scrolling sideways.
      if (tab.scrollIntoView) tab.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    }

    function onClick(e) {
      var tab = e.currentTarget;
      // Modified clicks keep their link meaning: open the collection.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      e.preventDefault();
      select(tab, false);
    }

    function onKey(e) {
      var i = tabs.indexOf(e.currentTarget);
      var next = null;
      if (e.key === "ArrowRight") next = tabs[(i + 1) % tabs.length];
      else if (e.key === "ArrowLeft") next = tabs[(i - 1 + tabs.length) % tabs.length];
      else if (e.key === "Home") next = tabs[0];
      else if (e.key === "End") next = tabs[tabs.length - 1];
      if (!next) return;
      e.preventDefault();
      select(next, true);
    }

    tabs.forEach(function (t) {
      t.addEventListener("click", onClick);
      t.addEventListener("keydown", onKey);
    });

    return function () {
      tabs.forEach(function (t) {
        t.removeEventListener("click", onClick);
        t.removeEventListener("keydown", onKey);
      });
    };
  });

  /* ==================================================================
     MODULE: loadout
     Ties each numbered item in the list to its hotspot on the photograph:
     pointing at either lights both. Pure presentation — every hotspot and
     every row is already a working link without this.
     ================================================================== */

  window.ATG.register("loadout", function (root) {
    var nodes = Array.prototype.slice.call(root.querySelectorAll("[data-atg-spot]"));
    if (!nodes.length) return;

    function set(n) {
      nodes.forEach(function (el) {
        el.classList.toggle("is-active", n !== null && el.getAttribute("data-atg-spot") === n);
      });
      root.classList.toggle("has-active", n !== null);
    }
    function on(e) { set(e.currentTarget.getAttribute("data-atg-spot")); }
    function off() { set(null); }

    nodes.forEach(function (el) {
      el.addEventListener("mouseenter", on);
      el.addEventListener("focus", on);
      el.addEventListener("mouseleave", off);
      el.addEventListener("blur", off);
    });

    return function () {
      nodes.forEach(function (el) {
        el.removeEventListener("mouseenter", on);
        el.removeEventListener("focus", on);
        el.removeEventListener("mouseleave", off);
        el.removeEventListener("blur", off);
      });
    };
  });

  /* ==================================================================
     Shared: fetch a section's fresh HTML through Shopify's Section
     Rendering API. Resolves to the new section root element.
     ================================================================== */

  function fetchSection(url, sectionId) {
    var u = new URL(url, window.location.origin);
    u.searchParams.set("section_id", sectionId);
    return fetch(u.toString(), { headers: { "Accept": "text/html" } })
      .then(function (r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        return doc.querySelector("[data-section-id]") || doc.body.firstElementChild;
      });
  }

  /* ==================================================================
     Shared: the cart count in the header. Two places (the top row and
     the pinned bar); a badge is created if the cart was empty.
     ================================================================== */

  function setCartCount(n) {
    var links = document.querySelectorAll(".atg-header__cart, .atg-bar__end .atg-bar__icon:last-child");
    Array.prototype.forEach.call(links, function (a) {
      var badge = a.querySelector("[data-atg-cart-count]");
      if (n > 0) {
        if (!badge) {
          badge = document.createElement("span");
          badge.className = "atg-header__count";
          badge.setAttribute("data-atg-cart-count", "");
          a.appendChild(badge);
        }
        badge.textContent = n;
      } else if (badge) {
        badge.remove();
      }
      var label = a.getAttribute("aria-label");
      if (label) a.setAttribute("aria-label", label.replace(/\(\d+\)/, "(" + n + ")"));
    });
  }

  /* ==================================================================
     Shared: rail controls for an .atg-railwrap. The fades and arrows
     follow what is left to scroll on each side; a vertical mouse wheel
     scrolls sideways while the rail can still move that way (then lets
     the page have it); a mouse can drag it; the current item ([aria-
     current]) starts in view. Returns a teardown.
     ================================================================== */

  function rail(wrap) {
    var track = wrap.querySelector(".atg-rail");
    if (!track) return function () {};
    var prev = wrap.querySelector("[data-atg-rail-prev]");
    var next = wrap.querySelector("[data-atg-rail-next]");
    var offs = [];
    function on(el, type, fn, opts) {
      if (!el) return;
      el.addEventListener(type, fn, opts);
      offs.push(function () { el.removeEventListener(type, fn, opts); });
    }

    function update() {
      var max = track.scrollWidth - track.clientWidth;
      wrap.classList.toggle("can-left", track.scrollLeft > 2);
      wrap.classList.toggle("can-right", track.scrollLeft < max - 2);
    }

    function step(dir) {
      track.scrollBy({ left: dir * Math.max(160, track.clientWidth * 0.7), behavior: "smooth" });
    }
    on(prev, "click", function () { step(-1); });
    on(next, "click", function () { step(1); });
    on(track, "scroll", update, { passive: true });
    on(window, "resize", update);

    on(track, "wheel", function (e) {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      var max = track.scrollWidth - track.clientWidth;
      if (max <= 0) return;
      if ((e.deltaY < 0 && track.scrollLeft <= 0) || (e.deltaY > 0 && track.scrollLeft >= max - 1)) return;
      e.preventDefault();
      track.scrollLeft += e.deltaY;
    }, { passive: false });

    // Drag with a mouse. A drag of more than a few pixels swallows the
    // click that ends it, so letting go over a pill does not follow it.
    var startX = 0, startLeft = 0, dragging = false, moved = false;
    on(track, "pointerdown", function (e) {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      if (track.scrollWidth <= track.clientWidth) return;
      dragging = true; moved = false;
      startX = e.clientX; startLeft = track.scrollLeft;
    });
    on(window, "pointermove", function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 5) { moved = true; wrap.classList.add("is-dragging"); }
      if (moved) track.scrollLeft = startLeft - dx;
    });
    on(window, "pointerup", function () {
      if (!dragging) return;
      dragging = false;
      wrap.classList.remove("is-dragging");
      // The click that ends a drag fires right after this; clear the flag
      // once it has had its chance, so a later click is never swallowed.
      setTimeout(function () { moved = false; }, 0);
    });
    on(track, "click", function (e) {
      if (!moved) return;
      moved = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    var current = track.querySelector("[aria-current]");
    if (current) {
      var left = current.offsetLeft - (track.clientWidth - current.offsetWidth) / 2;
      track.scrollLeft = Math.max(0, left - track.offsetLeft);
    }
    update();

    return function () {
      offs.forEach(function (off) { off(); });
      offs.length = 0;
    };
  }

  /* ==================================================================
     MODULE: courses
     The course window. A card is a link (to its first date, or to the
     schedule) and this turns the click into opening the window instead.
     Native <dialog>: showModal() gives the backdrop, keeps focus inside
     and closes on Escape. On top of that: the page behind stops
     scrolling, a click on the backdrop closes, focus goes back to the
     card, and each date's form adds to the cart without leaving.
     ================================================================== */

  window.ATG.register("courses", function (root) {
    var openers = Array.prototype.slice.call(root.querySelectorAll("[data-atg-course-open]"));
    if (!openers.length) return;
    var listeners = [];
    var lastOpener = null;

    function on(el, type, fn) {
      el.addEventListener(type, fn);
      listeners.push(function () { el.removeEventListener(type, fn); });
    }

    function lock(v) { document.documentElement.style.overflow = v ? "hidden" : ""; }

    // Discipline filter: a pill shows the cards filed under it ("All"
    // shows every card). Cards carry their disciplines as "|cqb|rifle|".
    var filter = root.querySelector("[data-atg-course-filter]");
    if (filter) {
      listeners.push(rail(filter));
      var pills = Array.prototype.slice.call(filter.querySelectorAll("[data-atg-disc]"));
      var cards = Array.prototype.slice.call(root.querySelectorAll(".atg-ccard"));
      on(filter, "click", function (e) {
        var pill = e.target.closest && e.target.closest("[data-atg-disc]");
        if (!pill) return;
        var d = pill.getAttribute("data-atg-disc");
        pills.forEach(function (p) {
          var active = p === pill;
          p.classList.toggle("is-active", active);
          p.setAttribute("aria-pressed", active ? "true" : "false");
        });
        cards.forEach(function (c) {
          var ds = c.getAttribute("data-atg-disc") || "";
          c.hidden = !!d && ds.indexOf("|" + d + "|") < 0;
        });
      });
    }

    function addToCart(form) {
      var btn = form.querySelector("button[type=submit]");
      var row = form.closest(".atg-cdate") || form.parentNode;
      var old = row && row.querySelector(".atg-cdate__msg");
      if (old) old.remove();
      if (btn) { btn.disabled = true; btn.textContent = "Adding…"; }

      var addUrl = ((window.routes && window.routes.cart_add_url) || "/cart/add") + ".js";
      fetch(addUrl, { method: "POST", headers: { "Accept": "application/json" }, body: new FormData(form) })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error((res.body && (res.body.description || res.body.message)) || "Could not add to cart.");
          if (btn) { btn.textContent = "Added"; btn.classList.add("is-added"); }
          var view = document.createElement("a");
          view.className = "atg-cdate__more";
          view.href = (window.routes && window.routes.cart_url) || "/cart";
          view.textContent = "View cart";
          var more = form.parentNode.querySelector(".atg-cdate__more");
          if (more) more.replaceWith(view); else form.parentNode.appendChild(view);
          return fetch("/cart.js", { headers: { "Accept": "application/json" } })
            .then(function (r) { return r.json(); })
            .then(function (cart) { setCartCount(cart.item_count); });
        })
        .catch(function (err) {
          if (btn) { btn.disabled = false; btn.textContent = btn.getAttribute("data-atg-add-label") || "Add to cart"; }
          if (row) {
            var m = document.createElement("p");
            m.className = "atg-cdate__msg";
            m.setAttribute("role", "alert");
            m.textContent = err.message;
            row.appendChild(m);
          }
        });
    }

    openers.forEach(function (a) {
      var dlg = document.getElementById(a.getAttribute("data-atg-course-open"));
      if (!dlg || typeof dlg.showModal !== "function") return;

      on(a, "click", function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        lastOpener = a;
        dlg.showModal();
        lock(true);
        var sc = dlg.querySelector(".atg-cdlg__scroll");
        if (sc) sc.scrollTop = 0;
      });

      on(dlg, "close", function () {
        lock(false);
        if (lastOpener) lastOpener.focus({ preventScroll: true });
      });

      // A click on the <dialog> element itself, not its panel, is a click
      // on the backdrop.
      on(dlg, "click", function (e) { if (e.target === dlg) dlg.close(); });

      Array.prototype.forEach.call(dlg.querySelectorAll("[data-atg-dialog-close]"), function (b) {
        on(b, "click", function () { dlg.close(); });
      });

      // Date rows pick what the bar books: a click anywhere on a row picks
      // it, and the bar's date, price and button follow. A row's Details
      // button opens that date's own description underneath it. Picking a new date
      // after an add re-arms the button; the same date stays "Added".
      var barForm = dlg.querySelector(".atg-cdlg__buy[id]");
      var barWhen = dlg.querySelector("[data-atg-bar-when]");
      var barPrice = dlg.querySelector("[data-atg-bar-price]");
      on(dlg, "click", function (e) {
        var row = e.target.closest && e.target.closest("[data-atg-cdate]");
        var info = e.target.closest && e.target.closest("[data-atg-cdate-info]");
        if (info && info.tagName === "BUTTON") {
          var panel = document.getElementById(info.getAttribute("aria-controls"));
          var open = info.getAttribute("aria-expanded") !== "true";
          info.setAttribute("aria-expanded", open ? "true" : "false");
          if (panel) panel.hidden = !open;
          return;
        }
        if (!row || e.target.closest("a, input, label, button, .atg-cdate__detail")) return;
        // A row with options (rent / have my own) keeps the one already
        // picked; otherwise the row's first choice is picked.
        if (row.querySelector(".atg-cdate__radio:checked")) return;
        var r = row.querySelector(".atg-cdate__radio");
        if (r) { r.checked = true; r.dispatchEvent(new Event("change", { bubbles: true })); }
      });
      on(dlg, "change", function (e) {
        var r = e.target;
        if (!r.classList || !r.classList.contains("atg-cdate__radio")) return;
        if (barWhen) barWhen.textContent = r.getAttribute("data-label") || "";
        if (barPrice) barPrice.textContent = r.getAttribute("data-price") || "";
        var btn = barForm && barForm.querySelector("button[type=submit]");
        if (btn && btn.classList.contains("is-added")) {
          btn.classList.remove("is-added");
          btn.disabled = false;
          btn.textContent = btn.getAttribute("data-atg-add-label") || "Add to cart";
        }
        var msg = barForm && barForm.parentNode.querySelector(".atg-cdate__msg");
        if (msg) msg.remove();
      });

      Array.prototype.forEach.call(dlg.querySelectorAll("[data-atg-dialog-jump]"), function (j) {
        on(j, "click", function (e) {
          var target = document.getElementById(j.getAttribute("data-atg-dialog-jump"));
          if (!target) return;
          e.preventDefault();
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      });

      Array.prototype.forEach.call(dlg.querySelectorAll("form[data-atg-add]"), function (form) {
        on(form, "submit", function (e) {
          if (!window.fetch) return;
          e.preventDefault();
          addToCart(form);
        });
      });
    });

    return function () {
      listeners.forEach(function (off) { off(); });
      listeners.length = 0;
      lock(false);
    };
  });

  /* ==================================================================
     MODULE: shop
     Collection pages. Two modes, set by the section (data-mode):

     instant — every product of the collection is already on the page.
       Brand, price and stock filters and the sort run here, in place:
       the Brand list is built from the cards, so it is always this
       section's own brands, with counts that follow the other filters.
       State lives in the query string (?brand=A,B&min=&max=&stock=1&
       sort=) so a view can be shared and survives a reload.

     paged — over 250 products. Shopify's own filters: a form change
       fetches this section through the Section Rendering API and swaps
       it in.

     In both, a category pill fetches the next collection's section and
     swaps it in, so moving between sections never reloads the page.
     ================================================================== */

  window.ATG.register("shop", function (root) {
    var sectionId = root.getAttribute("data-section-id");
    if (!sectionId || !window.fetch || !window.DOMParser) return;
    var busy = null;
    var teardownInstant = null;
    var teardownRail = null;

    var money = (function () {
      try {
        var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
        var f = new Intl.NumberFormat(undefined, { style: "currency", currency: cur, maximumFractionDigits: 0 });
        return function (cents) { return f.format(cents / 100); };
      } catch (e) {
        return function (cents) { return "$" + Math.round(cents / 100); };
      }
    })();

    /* ---- instant mode ------------------------------------------------ */

    function setupInstant() {
      var grid = root.querySelector("[data-atg-grid]");
      var tools = root.querySelector("[data-atg-tools]");
      if (!grid || !tools) return null;

      var cards = Array.prototype.slice.call(grid.querySelectorAll("[data-atg-item]"));
      var brandList = root.querySelector("[data-atg-brands]");
      var minIn = root.querySelector("[data-atg-min]");
      var maxIn = root.querySelector("[data-atg-max]");
      var stockIn = root.querySelector("[data-atg-stock]");
      var sortIn = root.querySelector("[data-atg-sort]");
      var chips = root.querySelector("[data-atg-chips]");
      var count = root.querySelector("[data-atg-count]");
      var empty = root.querySelector("[data-atg-empty]");
      var moreWrap = root.querySelector("[data-atg-more-wrap]");
      var shownLabel = root.querySelector("[data-atg-shown]");
      var nBrand = root.querySelector('[data-atg-n="brand"]');
      var nPrice = root.querySelector('[data-atg-n="price"]');
      var rangeHint = root.querySelector("[data-atg-range]");
      var pageSize = parseInt(root.getAttribute("data-page-size"), 10) || 24;
      var OTHER = "Other";

      var items = cards.map(function (el) {
        return {
          el: el,
          i: +el.getAttribute("data-i"),
          brand: el.getAttribute("data-brand") || OTHER,
          price: +el.getAttribute("data-price") || 0,
          avail: el.getAttribute("data-avail") === "1",
          created: +el.getAttribute("data-created") || 0,
          title: (el.getAttribute("data-title") || "").toLowerCase()
        };
      });

      var lo = Math.min.apply(null, items.map(function (x) { return x.price; }));
      var hi = Math.max.apply(null, items.map(function (x) { return x.price; }));
      if (rangeHint && items.length) rangeHint.textContent = "This section: " + money(lo) + " – " + money(hi);
      if (minIn) minIn.placeholder = Math.floor(lo / 100);
      if (maxIn) maxIn.placeholder = Math.ceil(hi / 100);

      // State from the address bar.
      var q = new URLSearchParams(window.location.search);
      var state = {
        brands: (q.get("brand") || "").split(",").filter(Boolean),
        min: q.get("min") || "",
        max: q.get("max") || "",
        stock: q.get("stock") === "1",
        sort: q.get("sort") || "featured",
        shown: pageSize
      };
      if (minIn) minIn.value = state.min;
      if (maxIn) maxIn.value = state.max;
      if (stockIn) stockIn.checked = state.stock;
      if (sortIn) {
        sortIn.value = state.sort;
        if (sortIn.value !== state.sort) { state.sort = "featured"; sortIn.value = "featured"; }
      }

      function inPrice(x) {
        var mn = state.min === "" ? -Infinity : parseFloat(state.min) * 100;
        var mx = state.max === "" ? Infinity : parseFloat(state.max) * 100;
        return x.price >= mn && x.price <= mx;
      }
      function inStock(x) { return !state.stock || x.avail; }
      function inBrand(x) { return !state.brands.length || state.brands.indexOf(x.brand) !== -1; }

      // Brand list: every brand in this section, counted against the price
      // and stock filters so a count never promises what a click won't show.
      function renderBrands() {
        if (!brandList) return;
        var counts = {};
        items.forEach(function (x) { counts[x.brand] = counts[x.brand] || 0; });
        items.forEach(function (x) { if (inPrice(x) && inStock(x)) counts[x.brand] += 1; });
        var names = Object.keys(counts).sort(function (a, b) {
          if (a === OTHER) return 1;
          if (b === OTHER) return -1;
          return a.localeCompare(b);
        });
        brandList.innerHTML = "";
        names.forEach(function (name) {
          var li = document.createElement("li");
          var label = document.createElement("label");
          var on = state.brands.indexOf(name) !== -1;
          label.className = "atg-drop__opt" + (counts[name] === 0 && !on ? " is-empty" : "");
          var input = document.createElement("input");
          input.type = "checkbox";
          input.value = name;
          input.checked = on;
          input.disabled = counts[name] === 0 && !on;
          input.setAttribute("data-atg-brand", "");
          var box = document.createElement("span"); box.className = "atg-drop__box"; box.setAttribute("aria-hidden", "true");
          var text = document.createElement("span"); text.className = "atg-drop__label"; text.textContent = name;
          var n = document.createElement("span"); n.className = "atg-drop__count"; n.textContent = counts[name];
          label.appendChild(input); label.appendChild(box); label.appendChild(text); label.appendChild(n);
          li.appendChild(label);
          brandList.appendChild(li);
        });
        // A section with a single brand has nothing to choose between.
        var drop = brandList.closest(".atg-drop");
        if (drop) drop.hidden = names.length < 2;
      }

      var sorters = {
        featured: function (a, b) { return a.i - b.i; },
        "brand-asc": function (a, b) { return (a.brand === OTHER) - (b.brand === OTHER) || a.brand.localeCompare(b.brand) || a.title.localeCompare(b.title); },
        "brand-desc": function (a, b) { return (a.brand === OTHER) - (b.brand === OTHER) || b.brand.localeCompare(a.brand) || a.title.localeCompare(b.title); },
        "price-asc": function (a, b) { return a.price - b.price || a.i - b.i; },
        "price-desc": function (a, b) { return b.price - a.price || a.i - b.i; },
        newest: function (a, b) { return b.created - a.created || a.i - b.i; },
        "title-asc": function (a, b) { return a.title.localeCompare(b.title); }
      };

      function chip(label, onRemove) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "atg-pill atg-pill--outline atg-shop__chip";
        b.innerHTML = "<span></span><span aria-hidden=\"true\">&times;</span>";
        b.firstChild.textContent = label;
        b.setAttribute("aria-label", "Remove " + label);
        b.addEventListener("click", onRemove);
        return b;
      }

      function renderChips() {
        if (!chips) return;
        chips.innerHTML = "";
        state.brands.forEach(function (name) {
          chips.appendChild(chip(name, function () {
            state.brands = state.brands.filter(function (b) { return b !== name; });
            apply(true);
          }));
        });
        if (state.min !== "" || state.max !== "") {
          var lab = (state.min !== "" ? money(parseFloat(state.min) * 100) : money(lo)) + " – " +
                    (state.max !== "" ? money(parseFloat(state.max) * 100) : money(hi));
          chips.appendChild(chip(lab, function () {
            state.min = ""; state.max = "";
            if (minIn) minIn.value = ""; if (maxIn) maxIn.value = "";
            apply(true);
          }));
        }
        if (state.stock) {
          chips.appendChild(chip("In stock", function () {
            state.stock = false; if (stockIn) stockIn.checked = false; apply(true);
          }));
        }
        var any = chips.children.length > 0;
        if (any) {
          var clear = document.createElement("button");
          clear.type = "button";
          clear.className = "atg-shop__clear";
          clear.textContent = "Clear all";
          clear.addEventListener("click", clearAll);
          chips.appendChild(clear);
        }
        chips.hidden = !any;
      }

      function syncUrl() {
        var p = new URLSearchParams(window.location.search);
        ["brand", "min", "max", "stock", "sort"].forEach(function (k) { p.delete(k); });
        if (state.brands.length) p.set("brand", state.brands.join(","));
        if (state.min !== "") p.set("min", state.min);
        if (state.max !== "") p.set("max", state.max);
        if (state.stock) p.set("stock", "1");
        if (state.sort !== "featured") p.set("sort", state.sort);
        var qs = p.toString();
        window.history.replaceState(window.history.state, "", window.location.pathname + (qs ? "?" + qs : ""));
      }

      function apply(resetPage) {
        if (resetPage) state.shown = pageSize;
        var match = items.filter(function (x) { return inBrand(x) && inPrice(x) && inStock(x); });
        match.sort(sorters[state.sort] || sorters.featured);

        // Reorder: matches first in sorted order, the rest after them hidden.
        var matchSet = new Set(match);
        var frag = document.createDocumentFragment();
        match.forEach(function (x, n) {
          x.el.hidden = n >= state.shown;
          x.el.classList.add("is-revealed");
          frag.appendChild(x.el);
        });
        items.forEach(function (x) {
          if (!matchSet.has(x)) { x.el.hidden = true; frag.appendChild(x.el); }
        });
        grid.appendChild(frag);

        if (count) count.textContent = match.length + (match.length === 1 ? " product" : " products");
        if (empty) empty.hidden = match.length > 0;
        grid.hidden = match.length === 0;
        if (moreWrap) {
          moreWrap.hidden = match.length <= state.shown;
          if (shownLabel) shownLabel.textContent = Math.min(state.shown, match.length) + " of " + match.length;
        }
        if (nBrand) { nBrand.hidden = !state.brands.length; nBrand.textContent = state.brands.length; }
        if (nPrice) nPrice.hidden = state.min === "" && state.max === "";
        var brandSummary = nBrand && nBrand.closest("summary");
        if (brandSummary) brandSummary.classList.toggle("is-active", state.brands.length > 0);
        var priceSummary = nPrice && nPrice.closest("summary");
        if (priceSummary) priceSummary.classList.toggle("is-active", !(state.min === "" && state.max === ""));
        var toggle = stockIn && stockIn.closest(".atg-shop__toggle");
        if (toggle) toggle.classList.toggle("is-active", state.stock);

        renderBrands();
        renderChips();
        syncUrl();
      }

      function clearAll() {
        state.brands = []; state.min = ""; state.max = ""; state.stock = false;
        if (minIn) minIn.value = ""; if (maxIn) maxIn.value = "";
        if (stockIn) stockIn.checked = false;
        apply(true);
      }

      function onChange(e) {
        var t = e.target;
        if (t.hasAttribute("data-atg-brand")) {
          state.brands = Array.prototype.map.call(root.querySelectorAll("[data-atg-brand]:checked"), function (i) { return i.value; });
          apply(true);
        } else if (t === minIn || t === maxIn) {
          state.min = minIn ? minIn.value.trim() : "";
          state.max = maxIn ? maxIn.value.trim() : "";
          apply(true);
        } else if (t === stockIn) {
          state.stock = stockIn.checked; apply(true);
        } else if (t === sortIn) {
          state.sort = sortIn.value; apply(false);
        }
      }
      function onKey(e) {
        if (e.key === "Enter" && (e.target === minIn || e.target === maxIn)) {
          e.preventDefault();
          e.target.blur();
        }
      }
      function onClick(e) {
        if (e.target.closest("[data-atg-more]")) { state.shown += pageSize; apply(false); }
        else if (e.target.closest("[data-atg-clear]")) clearAll();
      }

      root.addEventListener("change", onChange);
      root.addEventListener("keydown", onKey);
      root.addEventListener("click", onClick);
      tools.hidden = false;
      apply(false);

      return function () {
        root.removeEventListener("change", onChange);
        root.removeEventListener("keydown", onKey);
        root.removeEventListener("click", onClick);
      };
    }

    /* ---- shared: swap in another render of this section --------------- */

    function openKeys() {
      return Array.prototype.map.call(root.querySelectorAll("details[open][data-atg-drop]"), function (d) {
        return d.getAttribute("data-atg-drop");
      });
    }

    function setup() {
      if (teardownInstant) { teardownInstant(); teardownInstant = null; }
      if (root.getAttribute("data-mode") === "instant") teardownInstant = setupInstant();
      if (teardownRail) { teardownRail(); teardownRail = null; }
      var wrap = root.querySelector("[data-atg-railwrap]");
      if (wrap) teardownRail = rail(wrap);
    }

    function render(url, push, keepOpen) {
      var keep = keepOpen ? openKeys() : [];
      root.classList.add("is-loading");
      var token = {};
      busy = token;
      fetchSection(url, sectionId)
        .then(function (fresh) {
          if (busy !== token || !fresh) return;
          if (push) window.history.pushState({ atgShop: true }, "", url);
          root.innerHTML = fresh.innerHTML;
          root.setAttribute("data-mode", fresh.getAttribute("data-mode") || "instant");
          root.classList.remove("is-loading");
          keep.forEach(function (k) {
            Array.prototype.forEach.call(root.querySelectorAll("details[data-atg-drop]"), function (d) {
              if (d.getAttribute("data-atg-drop") === k) d.open = true;
            });
          });
          Array.prototype.forEach.call(root.querySelectorAll("[data-atg-reveal]"), function (el) { el.classList.add("is-revealed"); });
          setup();
        })
        .catch(function () { window.location.href = url; });
    }

    /* ---- paged mode: Shopify's filters through the form ---------------- */

    function formUrl(form) {
      var params = new URLSearchParams();
      new FormData(form).forEach(function (v, k) { if (v !== "" && v !== null) params.append(k, v); });
      var qs = params.toString();
      return form.getAttribute("action") + (qs ? "?" + qs : "");
    }
    function onFormChange(e) {
      var form = e.target.closest && e.target.closest("form[data-atg-shop-form]");
      if (form) render(formUrl(form), true, true);
    }
    function onSubmit(e) {
      var form = e.target;
      if (!form.matches || !form.matches("form[data-atg-shop-form]")) return;
      e.preventDefault();
      render(formUrl(form), true, true);
    }

    /* ---- links inside the section ------------------------------------- */

    function onLink(e) {
      var a = e.target.closest && e.target.closest("a[href]");
      if (!a || !root.contains(a)) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      if (!a.closest(".atg-shop__cats, .atg-shop__active, .atg-shop__pages, .atg-shop__empty")) return;
      var u = new URL(a.href, window.location.origin);
      if (u.origin !== window.location.origin) return;
      e.preventDefault();
      render(u.pathname + u.search, true, false);
      if (a.closest(".atg-shop__pages, .atg-shop__cats")) root.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    function onDocClick(e) {
      Array.prototype.forEach.call(root.querySelectorAll("details[open][data-atg-drop]"), function (d) {
        if (!d.contains(e.target)) d.open = false;
      });
    }

    function onPop() { render(window.location.pathname + window.location.search, false, false); }

    root.addEventListener("change", onFormChange);
    root.addEventListener("submit", onSubmit);
    root.addEventListener("click", onLink);
    document.addEventListener("click", onDocClick);
    window.addEventListener("popstate", onPop);
    setup();

    return function () {
      if (teardownInstant) teardownInstant();
      if (teardownRail) teardownRail();
      root.removeEventListener("change", onFormChange);
      root.removeEventListener("submit", onSubmit);
      root.removeEventListener("click", onLink);
      document.removeEventListener("click", onDocClick);
      window.removeEventListener("popstate", onPop);
    };
  });

  /* ==================================================================
     MODULE: more
     "Load more" on the New list: fetches the next page of this section,
     appends its rows, and points the button at the page after.
     ================================================================== */

  window.ATG.register("more", function (root) {
    var sectionId = root.getAttribute("data-section-id");
    if (!sectionId || !window.fetch || !window.DOMParser) return;

    function onClick(e) {
      var a = e.target.closest && e.target.closest("[data-atg-more-link]");
      if (!a) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      e.preventDefault();
      root.classList.add("is-loading");
      fetchSection(a.href, sectionId)
        .then(function (fresh) {
          var list = root.querySelector("[data-atg-more-list]");
          var freshList = fresh && fresh.querySelector("[data-atg-more-list]");
          if (list && freshList) {
            Array.prototype.slice.call(freshList.children).forEach(function (li) {
              list.appendChild(document.importNode(li, true));
            });
          }
          var more = root.querySelector(".atg-newl__more");
          var freshMore = fresh && fresh.querySelector(".atg-newl__more");
          if (more) {
            if (freshMore) more.replaceWith(document.importNode(freshMore, true));
            else more.remove();
          }
          root.classList.remove("is-loading");
        })
        .catch(function () { window.location.href = a.href; });
    }

    root.addEventListener("click", onClick);
    return function () { root.removeEventListener("click", onClick); };
  });
  /* ==================================================================
     MODULE: product
     The product page. Three jobs:

     gallery  — one scroll-snap track of every photo: swipe on a phone,
                arrows and thumbnails on a desktop, arrow keys when the
                track has focus. The counter follows the scroll.
     variants — the option pills pick a variant from the JSON the section
                prints: price, stock label, button, the hidden id input
                and ?variant= in the address all follow, and the gallery
                moves to that variant's photo. Values that cannot be
                bought with the other choices are struck through.
     cart     — the form adds to the cart in place and bumps the header
                count. Without JavaScript it submits to /cart/add as usual.
     ================================================================== */

  window.ATG.register("product", function (root) {
    var listeners = [];
    var timers = [];
    function on(el, type, fn, opts) {
      if (!el) return;
      el.addEventListener(type, fn, opts);
      listeners.push(function () { el.removeEventListener(type, fn, opts); });
    }

    var data = {};
    var dataEl = root.querySelector("[data-atg-pdata]");
    try { data = JSON.parse(dataEl ? dataEl.textContent : "{}"); } catch (e) { data = {}; }
    var variants = data.variants || [];

    /* ---------- gallery ---------- */

    var track = root.querySelector("[data-atg-slides]");
    var slides = track ? Array.prototype.slice.call(track.children) : [];
    var thumbs = Array.prototype.slice.call(root.querySelectorAll("[data-atg-thumb]"));
    var thumbRail = root.querySelector(".atg-pdp__thumbs");
    var idxEl = root.querySelector("[data-atg-index]");
    var prev = root.querySelector("[data-atg-prev]");
    var next = root.querySelector("[data-atg-next]");
    var active = 0;

    function setActive(i) {
      active = i;
      if (idxEl) idxEl.textContent = i + 1;
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
      thumbs.forEach(function (t, k) {
        var isOn = k === i;
        t.classList.toggle("is-active", isOn);
        if (isOn) t.setAttribute("aria-current", "true"); else t.removeAttribute("aria-current");
        if (isOn && thumbRail && thumbRail.scrollWidth > thumbRail.clientWidth) {
          var li = t.parentNode;
          thumbRail.scrollTo({ left: li.offsetLeft - (thumbRail.clientWidth - li.offsetWidth) / 2, behavior: "smooth" });
        }
      });
      // A video that scrolls out of view stops.
      slides.forEach(function (s, k) {
        if (k === i) return;
        var v = s.querySelector("video");
        if (v && !v.paused) v.pause();
      });
    }

    function go(i, smooth) {
      if (!track || !slides.length) return;
      i = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth ? "smooth" : "auto" });
      setActive(i);
    }

    if (track && slides.length > 1) {
      var settle = null;
      on(track, "scroll", function () {
        clearTimeout(settle);
        settle = setTimeout(function () {
          var i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          if (i !== active) setActive(Math.max(0, Math.min(slides.length - 1, i)));
        }, 60);
      }, { passive: true });
      timers.push(function () { clearTimeout(settle); });

      on(prev, "click", function () { go(active - 1, true); });
      on(next, "click", function () { go(active + 1, true); });
      thumbs.forEach(function (t) {
        on(t, "click", function () { go(parseInt(t.getAttribute("data-atg-thumb"), 10) || 0, true); });
      });
      on(track, "keydown", function (e) {
        if (e.key === "ArrowRight") { e.preventDefault(); go(active + 1, true); }
        else if (e.key === "ArrowLeft") { e.preventDefault(); go(active - 1, true); }
      });
      setActive(0);
    }

    function goToMedia(id) {
      if (!id) return;
      for (var k = 0; k < slides.length; k++) {
        if (slides[k].getAttribute("data-media-id") === String(id)) { go(k, true); return; }
      }
    }

    /* ---------- variants ---------- */

    var form = root.querySelector("form[data-atg-pform]");
    if (!form) {
      return function () {
        listeners.forEach(function (off) { off(); });
        timers.forEach(function (t) { t(); });
      };
    }

    var idInput = form.querySelector("[data-atg-variant-id]");
    var fieldsets = Array.prototype.slice.call(form.querySelectorAll("[data-atg-opt]"));
    var btn = form.querySelector("[data-atg-add-btn]");
    var btnText = form.querySelector("[data-atg-add-text]");
    var addLabel = (btn && btn.getAttribute("data-atg-add-label")) || "Add to cart";
    var msg = form.querySelector("[data-atg-msg]");
    var priceEl = root.querySelector("[data-atg-price]");
    var compareEl = root.querySelector("[data-atg-compare]");
    var priceBox = root.querySelector("[data-atg-pricebox]");
    var stockEl = root.querySelector("[data-atg-stock]");
    var stockText = root.querySelector("[data-atg-stock-text]");
    var current = null;

    function money(cents) {
      var fmt = data.money || "${{amount}}";
      var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
      var kind = m ? m[1] : "amount";
      function f(dec, th, ds) {
        var p = (cents / 100).toFixed(dec).split(".");
        p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
        return p.join(ds);
      }
      var out;
      if (kind === "amount_no_decimals") out = f(0, ",", ".");
      else if (kind === "amount_with_comma_separator") out = f(2, ".", ",");
      else if (kind === "amount_no_decimals_with_comma_separator") out = f(0, ".", ",");
      else if (kind === "amount_with_apostrophe_separator") out = f(2, "'", ".");
      else out = f(2, ",", ".");
      var tmp = document.createElement("div");
      tmp.innerHTML = fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
      return tmp.textContent;
    }

    function chosen() {
      return fieldsets.map(function (fs) {
        var c = fs.querySelector("input:checked");
        return c ? c.value : null;
      });
    }

    function matches(v, opts) {
      for (var k = 0; k < opts.length; k++) if (v.options[k] !== opts[k]) return false;
      return true;
    }

    function find(opts) {
      for (var k = 0; k < variants.length; k++) if (matches(variants[k], opts)) return variants[k];
      return null;
    }

    function markAvailability(opts) {
      fieldsets.forEach(function (fs, k) {
        Array.prototype.forEach.call(fs.querySelectorAll("input"), function (inp) {
          var test = opts.slice();
          test[k] = inp.value;
          var ok = variants.some(function (v) { return v.available && matches(v, test); });
          var label = inp.nextElementSibling;
          if (label) label.classList.toggle("is-unavailable", !ok);
        });
        var shown = fs.querySelector("[data-atg-chosen]");
        if (shown) shown.textContent = opts[k] || "";
      });
    }

    function setButton(v) {
      if (!btn) return;
      btn.classList.remove("is-added");
      if (!v) { btn.disabled = true; btnText.textContent = "Unavailable"; }
      else if (!v.available) { btn.disabled = true; btnText.textContent = "Sold out"; }
      else { btn.disabled = false; btnText.textContent = addLabel; }
    }

    function apply(v, moveGallery) {
      current = v;
      form.classList.toggle("is-soldout", !v || !v.available);
      if (msg) { msg.textContent = ""; msg.classList.remove("is-error"); }
      setButton(v);
      if (!v) {
        if (stockEl) stockEl.classList.add("is-out");
        if (stockText) stockText.textContent = "Unavailable";
        return;
      }

      if (idInput && idInput.value !== String(v.id)) {
        idInput.value = v.id;
        // Apps that watch the product form (Sesami) listen for this.
        idInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      if (priceEl) priceEl.textContent = money(v.price);
      var sale = v.compare_at_price && v.compare_at_price > v.price;
      if (compareEl) {
        compareEl.hidden = !sale;
        if (sale) compareEl.textContent = money(v.compare_at_price);
      }
      if (priceBox) priceBox.classList.toggle("atg-price--sale", !!sale);

      if (stockEl) stockEl.classList.toggle("is-out", !v.available);
      if (stockText) stockText.textContent = v.available ? "In stock" : "Sold out";

      if (window.history && window.history.replaceState) {
        var u = new URL(window.location.href);
        u.searchParams.set("variant", v.id);
        window.history.replaceState(window.history.state, "", u.toString());
      }

      if (moveGallery && v.featured_media) goToMedia(v.featured_media.id);

      document.dispatchEvent(new CustomEvent("atg:variant", { detail: { variant: v, form: form } }));
    }

    on(form, "change", function (e) {
      if (!e.target.classList || !e.target.classList.contains("atg-pdp__radio")) return;
      var opts = chosen();
      markAvailability(opts);
      apply(find(opts), true);
    });

    if (fieldsets.length) {
      var start = chosen();
      markAvailability(start);
      current = find(start);
      if (current && current.featured_media) {
        for (var k = 0; k < slides.length; k++) {
          if (slides[k].getAttribute("data-media-id") === String(current.featured_media.id)) { go(k, false); break; }
        }
      }
    }

    /* ---------- quantity ---------- */

    Array.prototype.forEach.call(form.querySelectorAll("[data-atg-step]"), function (b) {
      on(b, "click", function () {
        var input = form.querySelector("input[name=quantity]");
        if (!input) return;
        var n = (parseInt(input.value, 10) || 1) + parseInt(b.getAttribute("data-atg-step"), 10);
        input.value = Math.max(1, n);
      });
    });

    /* ---------- add to cart ---------- */

    var resetTimer = null;
    timers.push(function () { clearTimeout(resetTimer); });

    on(form, "submit", function (e) {
      if (!window.fetch || !btn || btn.disabled) return;
      e.preventDefault();
      btn.disabled = true;
      btnText.textContent = "Adding…";
      if (msg) { msg.textContent = ""; msg.classList.remove("is-error"); }

      var addUrl = ((window.routes && window.routes.cart_add_url) || "/cart/add") + ".js";
      fetch(addUrl, { method: "POST", headers: { "Accept": "application/json" }, body: new FormData(form) })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error((res.body && (res.body.description || res.body.message)) || "Could not add to cart.");
          btn.classList.add("is-added");
          btnText.textContent = "Added";
          if (msg) {
            msg.textContent = "Added to your cart. ";
            var a = document.createElement("a");
            a.href = (window.routes && window.routes.cart_url) || "/cart";
            a.textContent = "View cart";
            msg.appendChild(a);
          }
          clearTimeout(resetTimer);
          resetTimer = setTimeout(function () { setButton(current || { available: true }); }, 2400);
          return fetch("/cart.js", { headers: { "Accept": "application/json" } })
            .then(function (r) { return r.json(); })
            .then(function (cart) { setCartCount(cart.item_count); });
        })
        .catch(function (err) {
          setButton(current || { available: true });
          if (msg) { msg.textContent = err.message; msg.classList.add("is-error"); }
        });
    });

    return function () {
      listeners.forEach(function (off) { off(); });
      listeners.length = 0;
      timers.forEach(function (t) { t(); });
    };
  });

  /* ==================================================================
     MODULE: pdetails
     The product details section (Specs / Details / Support, then FAQs).

     tabs     — a WAI-ARIA tablist: click or arrow keys switch panels, the
                underline slides to the chosen tab. Specs opens first
                unless it has fewer than three rows and there is a
                description, in which case Details does.
     specs    — supplier descriptions carry lines like "Run Time: 1.50
                hours". Those are lifted out of the Details text into the
                Specs grid (a line whose label is already there
                is left alone — the store's own data wins), and a heading
                left empty is dropped.
     overview — the description's opening paragraph (or, failing that,
                its first short list) is copied into the main product
                section, under the price, with a "Full details" link that
                opens the Details tab here.
     ================================================================== */

  window.ATG.register("pdetails", function (root) {
    var listeners = [];
    function on(el, type, fn, opts) {
      if (!el) return;
      el.addEventListener(type, fn, opts);
      listeners.push(function () { el.removeEventListener(type, fn, opts); });
    }
    function textOf(el) { return (el.textContent || "").replace(/\s+/g, " ").trim(); }

    var desc = root.querySelector("[data-atg-desc]");
    var specs = root.querySelector("[data-atg-specs]");
    var BLOCKS = "p,div,li,ul,ol,table,h1,h2,h3,h4,h5,h6,section,article";

    function pruneEmpty(el) {
      while (el && el !== desc && el.parentNode && !textOf(el) && !el.querySelector("img,iframe,video")) {
        var up = el.parentNode;
        up.removeChild(el);
        el = up;
      }
    }

    /* ---------- specs lifted out of the description ---------- */

    if (desc && specs) {
      var rows = [];
      Array.prototype.forEach.call(desc.querySelectorAll("li, p, div, td"), function (el) {
        if (el.querySelector(BLOCKS)) return;
        var txt = textOf(el);
        if (txt.length < 4 || txt.length > 90) return;
        var m = txt.match(/^([^:]{2,32}):\s*(\S.*)$/);
        if (!m || /https?$/i.test(m[1])) return;
        rows.push({ el: el, k: m[1].replace(/^[•\-–·\s]+/, "").trim(), v: m[2].trim() });
      });
      if (rows.length >= 3) {
        var existing = {};
        Array.prototype.forEach.call(specs.querySelectorAll("dt"), function (dt) { existing[textOf(dt).toLowerCase()] = dt.nextElementSibling; });
        rows.slice(0, 18).forEach(function (r) {
          var key = r.k.toLowerCase();
          // The store's own rows (brand, options, item no.) win over the
          // supplier's text: a matching line is only taken out of Details.
          if (!existing[key]) {
            var d = document.createElement("div");
            d.className = "atg-pd__spec";
            var dt = document.createElement("dt");
            dt.textContent = r.k;
            var dd = document.createElement("dd");
            dd.textContent = r.v;
            d.appendChild(dt);
            d.appendChild(dd);
            specs.appendChild(d);
            existing[key] = dd;
          }
          var parent = r.el.parentNode;
          parent.removeChild(r.el);
          pruneEmpty(parent);
        });
        Array.prototype.forEach.call(desc.querySelectorAll("h1,h2,h3,h4,h5,h6"), function (h) {
          if (!/spec/i.test(textOf(h))) return;
          var n = h.nextElementSibling;
          while (n && !textOf(n) && !n.querySelector("img,iframe")) n = n.nextElementSibling;
          if (!n) { var up = h.parentNode; up.removeChild(h); pruneEmpty(up); }
        });
      }
    }

    /* ---------- tabs ---------- */

    var tablist = root.querySelector("[data-atg-tabs]");
    var tabs = tablist ? Array.prototype.slice.call(tablist.querySelectorAll("[data-atg-tab]")) : [];
    var bar = root.querySelector("[data-atg-tabbar]");

    function panelFor(tab) { return root.querySelector('[data-atg-panel="' + tab.getAttribute("data-atg-tab") + '"]'); }

    function moveBar(tab) {
      if (!bar || !tab) return;
      bar.style.setProperty("--atg-tab-x", tab.offsetLeft + "px");
      bar.style.setProperty("--atg-tab-w", tab.offsetWidth + "px");
    }

    function select(tab, focus) {
      tabs.forEach(function (t) {
        var onTab = t === tab;
        t.setAttribute("aria-selected", onTab ? "true" : "false");
        t.tabIndex = onTab ? 0 : -1;
        var p = panelFor(t);
        if (p) p.hidden = !onTab;
      });
      moveBar(tab);
      if (focus) tab.focus();
    }

    function byName(name) {
      for (var i = 0; i < tabs.length; i++) if (tabs[i].getAttribute("data-atg-tab") === name) return tabs[i];
      return null;
    }

    if (tabs.length) {
      tablist.hidden = false;
      var start = tabs[0];
      if (specs && specs.children.length < 3 && byName("details")) start = byName("details");
      if (specs && specs.children.length === 0 && !byName("details")) start = tabs[1] || tabs[0];
      select(start, false);

      tabs.forEach(function (t, i) {
        on(t, "click", function () { select(t, false); });
        on(t, "keydown", function (e) {
          var n = null;
          if (e.key === "ArrowRight") n = tabs[(i + 1) % tabs.length];
          else if (e.key === "ArrowLeft") n = tabs[(i - 1 + tabs.length) % tabs.length];
          else if (e.key === "Home") n = tabs[0];
          else if (e.key === "End") n = tabs[tabs.length - 1];
          if (n) { e.preventDefault(); select(n, true); }
        });
      });
      on(window, "resize", function () {
        var cur = tablist.querySelector('[aria-selected="true"]');
        if (cur) moveBar(cur);
      });
    }

    /* ---------- overview under the price ---------- */

    var overview = document.querySelector("[data-atg-overview]");
    if (desc && overview && !overview.childNodes.length) {
      var picked = [];
      var chars = 0;
      Array.prototype.some.call(desc.querySelectorAll("p"), function (p) {
        var txt = textOf(p);
        if (txt.length < 60 || p.closest("table")) return false;
        picked.push(p);
        chars += txt.length;
        return picked.length >= 2 || chars > 380;
      });
      if (!picked.length) {
        Array.prototype.some.call(desc.querySelectorAll("ul, ol"), function (list) {
          var items = list.querySelectorAll(":scope > li");
          if (items.length < 2 || items.length > 8) return false;
          if (Array.prototype.some.call(items, function (li) { return textOf(li).length > 160 || li.querySelector("ul,ol"); })) return false;
          picked.push(list);
          return true;
        });
      }
      if (picked.length) {
        picked.forEach(function (el) {
          var c = el.cloneNode(true);
          [c].concat(Array.prototype.slice.call(c.querySelectorAll("*"))).forEach(function (n) {
            n.removeAttribute("style");
            n.removeAttribute("class");
            n.removeAttribute("id");
          });
          overview.appendChild(c);
        });
        var detailsTab = byName("details");
        if (detailsTab) {
          var more = document.createElement("a");
          more.className = "atg-pdp__more";
          more.href = "#details";
          more.textContent = "Full details";
          on(more, "click", function (e) {
            e.preventDefault();
            select(detailsTab, false);
            root.scrollIntoView({ behavior: "smooth", block: "start" });
          });
          overview.appendChild(more);
        }
        overview.hidden = false;
      }
    }

    /* ---------- the selected variant's item number ---------- */

    var skuEl = root.querySelector("[data-atg-sku]");
    on(document, "atg:variant", function (e) {
      var v = e.detail && e.detail.variant;
      if (!skuEl || !v) return;
      var sku = (v.sku || "").trim();
      if (sku && !(sku.length > 14 && /^\d+$/.test(sku))) skuEl.textContent = sku;
    });

    return function () {
      listeners.forEach(function (off) { off(); });
      listeners.length = 0;
      if (overview) { overview.innerHTML = ""; overview.hidden = true; }
    };
  });

  /* ==================================================================
     MODULE: cart
     The cart page. − / + / typing a quantity / Remove change the line
     through /cart/change.js (line = its 1-based position), then this
     section is fetched again and swapped in, and the header count
     follows. A refusal from Shopify (not enough seats or stock) shows
     under that line. Without JavaScript the same controls post the form.
     ================================================================== */

  window.ATG.register("cart", function (root) {
    var sectionId = root.getAttribute("data-section-id");
    if (!sectionId || !window.fetch) return;
    var busy = false;
    var typing = null;

    function lineOf(el) {
      var item = el.closest(".atg-cart__item");
      if (!item) return 0;
      var items = Array.prototype.slice.call(root.querySelectorAll(".atg-cart__item"));
      return items.indexOf(item) + 1;
    }

    function showError(line, msg) {
      var item = root.querySelectorAll(".atg-cart__item")[line - 1];
      var err = item && item.querySelector("[data-atg-cart-err]");
      if (!err) return;
      err.textContent = msg;
      err.hidden = false;
    }

    function change(line, qty) {
      if (busy || !line) return;
      busy = true;
      root.setAttribute("aria-busy", "true");
      fetch(((window.routes && window.routes.cart_change_url) || "/cart/change") + ".js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ line: line, quantity: Math.max(0, qty) })
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error((res.body && (res.body.description || res.body.message)) || "Could not update the cart.");
          setCartCount(res.body.item_count);
          return fetchSection(window.location.pathname, sectionId).then(function (fresh) {
            if (fresh) root.innerHTML = fresh.innerHTML;
          });
        })
        .catch(function (err) {
          busy = false;
          root.removeAttribute("aria-busy");
          // Put the number back to what the cart really holds.
          return fetchSection(window.location.pathname, sectionId).then(function (fresh) {
            if (fresh) root.innerHTML = fresh.innerHTML;
            showError(line, err.message);
          });
        })
        .then(function () {
          busy = false;
          root.removeAttribute("aria-busy");
        });
    }

    function onClick(e) {
      var step = e.target.closest && e.target.closest("[data-atg-cart-step]");
      if (step) {
        var input = step.parentNode.querySelector("[data-atg-cart-input]");
        var qty = (parseInt(input.value, 10) || 0) + parseInt(step.getAttribute("data-atg-cart-step"), 10);
        input.value = Math.max(0, qty);
        change(lineOf(step), qty);
        return;
      }
      var rm = e.target.closest && e.target.closest("[data-atg-cart-remove]");
      if (rm) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        change(lineOf(rm), 0);
      }
    }

    function onInput(e) {
      var input = e.target.closest && e.target.closest("[data-atg-cart-input]");
      if (!input) return;
      clearTimeout(typing);
      typing = setTimeout(function () {
        var qty = parseInt(input.value, 10);
        if (isNaN(qty)) return;
        change(lineOf(input), qty);
      }, 450);
    }

    // Enter in a quantity field would submit the form to checkout.
    function onKey(e) {
      if (e.key === "Enter" && e.target.matches && e.target.matches("[data-atg-cart-input]")) {
        e.preventDefault();
        clearTimeout(typing);
        change(lineOf(e.target), parseInt(e.target.value, 10) || 0);
      }
    }

    root.addEventListener("click", onClick);
    root.addEventListener("input", onInput);
    root.addEventListener("keydown", onKey);
    return function () {
      clearTimeout(typing);
      root.removeEventListener("click", onClick);
      root.removeEventListener("input", onInput);
      root.removeEventListener("keydown", onKey);
    };
  });

  /* ==================================================================
     MODULE: recs
     "You may also like". Asks Shopify's recommendations endpoint for
     this same section with the product id and swaps the cards in; the
     band stays hidden when there is nothing to recommend.
     ================================================================== */

  window.ATG.register("recs", function (root) {
    var url = root.getAttribute("data-url");
    if (!url || !window.fetch) return;
    var alive = true;

    fetch(url, { headers: { "Accept": "text/html" } })
      .then(function (r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.text(); })
      .then(function (html) {
        if (!alive) return;
        var doc = new DOMParser().parseFromString(html, "text/html");
        var fresh = doc.querySelector("[data-atg-recs-body]");
        var body = root.querySelector("[data-atg-recs-body]");
        if (!body || !fresh || !fresh.querySelector("[data-atg-reveal], article")) {
          if (!body || !body.children.length) root.classList.add("is-empty");
          return;
        }
        var node = document.importNode(fresh, true);
        body.replaceWith(node);
        root.classList.remove("is-empty");
        setTimeout(function () {
          Array.prototype.forEach.call(node.querySelectorAll("[data-atg-reveal]"), function (el) {
            el.classList.add("is-revealed");
          });
        }, 30);
      })
      .catch(function () {
        var body = root.querySelector("[data-atg-recs-body]");
        if (!body || !body.children.length) root.classList.add("is-empty");
      });

    return function () { alive = false; };
  });
  /* ==================================================================
     MODULE: downloads
     Download — a file on the store's own domain saves natively through
     the download attribute. One picked from Files is on Shopify's CDN,
     where browsers ignore that attribute and open the image instead, so
     it is fetched and saved as a blob (the CDN sends CORS headers). If
     that fails the link simply opens the image.
     Print — opens the target alone on a page sized to it (landscape for
     a landscape sheet) and brings up the print dialog.
     ================================================================== */

  window.ATG.register("downloads", function (root) {
    function onClick(e) {
      var dl = e.target.closest && e.target.closest("[data-atg-download]");
      if (dl) {
        var href = new URL(dl.href, window.location.href);
        if (href.origin === window.location.origin || !window.fetch || !window.URL.createObjectURL) return;
        e.preventDefault();
        dl.classList.add("is-busy");
        fetch(href.toString())
          .then(function (r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.blob(); })
          .then(function (blob) {
            var u = URL.createObjectURL(blob);
            var a = document.createElement("a");
            a.href = u;
            a.download = dl.getAttribute("download") || "target.jpg";
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(function () { URL.revokeObjectURL(u); }, 4000);
          })
          .catch(function () { window.open(href.toString(), "_blank", "noopener"); })
          .then(function () { dl.classList.remove("is-busy"); });
        return;
      }

      var pr = e.target.closest && e.target.closest("[data-atg-print]");
      if (!pr) return;
      var src = new URL(pr.getAttribute("data-atg-print"), window.location.href).toString();
      var landscape = pr.getAttribute("data-landscape") === "true";
      var w = window.open("", "_blank");
      if (!w) { window.location.href = src; return; }
      var esc = function (s) { return String(s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); };
      w.document.write(
        "<!doctype html><html><head><meta charset='utf-8'><title>" + esc(pr.getAttribute("data-title") || "Target") + "</title>" +
        "<style>@page{size:" + (landscape ? "landscape" : "portrait") + ";margin:0.25in}" +
        "html,body{margin:0;height:100%}body{display:flex;align-items:center;justify-content:center}" +
        "img{max-width:100%;max-height:100vh;object-fit:contain}</style></head>" +
        "<body><img src='" + esc(src) + "' onload='setTimeout(function(){window.focus();window.print();},150)'></body></html>"
      );
      w.document.close();
    }

    root.addEventListener("click", onClick);
    return function () { root.removeEventListener("click", onClick); };
  });
})();
